import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import DriverLayout from '@/views/driver/DriverLayout.vue';
import DriverLogin from '@/components/driver/DriverLogin.vue';
import DriverDashboard from '@/components/driver/DriverDashboard.vue';

Vue.use(VueRouter);

const routes = [
  // Driver Routes - Separate interface for drivers with its own layout
  {
    path: '/driver/login',
    name: 'driver-login',
    component: DriverLogin,
    meta: { isDriverRoute: true, requiresDriverAuth: false },
  },
  {
    path: '/driver',
    name: 'driver',
    component: DriverDashboard,
    meta: { isDriverRoute: true, requiresDriverAuth: true },
  },
  {
    path: '/landing',
    name: 'landing',
    component: () => import('@/views/LandingPageView.vue'),
  },
  {
    path: '/login',
    name: 'login',
    meta: { requiresUnAuth: true },
    component: () => import('@/views/LoginView.vue'),
  },
  {
    path: '/register',
    name: 'register',
    meta: { requiresUnAuth: true },
    component: () => import('@/views/RegisterAccountView.vue'),
  },
  {
    path: '/register/maybank-sme',
    name: 'register-maybank-sme',
    meta: { requiresUnAuth: true },
    component: () => import('@/views/RegisterAccountView.vue'),
  },
  // {
  //   path: '/register/:slug',
  //   name: 'register-with-invitation',
  //   meta: { requiresUnAuth: true },
  //   component: () => import('@/views/RegisterAccountView.vue'),
  //   props: true,
  // },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    meta: { requiresUnAuth: true },
    component: () => import('@/views/ForgotPasswordView.vue'),
  },
  {
    path: '/reset-password/:resetToken',
    name: 'reset-password',
    meta: { requiresUnAuth: true },
    component: () => import('@/views/ResetPasswordView.vue'),
    props: true,
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/HelpView.vue'),
  },
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'database',
        name: 'database',
        component: () => import('@/views/MarketplaceView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'material/:materialId',
        name: 'single-material',
        component: () => import('@/views/SingleMaterialView.vue'),
        meta: { requiresAuth: true },
        props: true,
      },
      {
        path: 'messages',
        name: 'messages',
        component: () => import('@/views/ChatView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'companies/:id',
        name: 'public-company',
        component: () => import('@/views/PublicCompanyView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'users/:id',
        name: 'public-user',
        component: () => import('@/views/PublicUserView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'account',
        name: 'account',
        component: () => import('@/views/AccountView.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: 'personal-info',
            name: 'personal-info',
            component: () => import('@/views/PersonalInfoView.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: 'manager-panel',
            name: 'manager-panel',
            component: () => import('@/views/ManagerPanelView.vue'),
            meta: { requiresAuth: true, requiresManager: true },
          },
          {
            path: 'manager-panel/offerings',
            name: 'offerings',
            component: () => import('@/views/ManagerPanelView.vue'),
            meta: { requiresAuth: true, requiresManager: true },
            props: { initialOption: 'offerings' },
          },
          {
            path: 'manager-panel/user-management',
            name: 'user-management',
            component: () => import('@/views/ManagerPanelView.vue'),
            meta: { requiresAuth: true, requiresManager: true },
            props: { initialOption: 'user-management' },
          },
          {
            path: 'manager-panel/donations',
            name: 'donations',
            component: () => import('@/views/ManagerPanelView.vue'),
            meta: { requiresAuth: true, requiresManager: true },
            props: { initialOption: 'donations' },
          },
          {
            path: 'dashboard',
            name: 'company-dashboard',
            component: () => import('@/views/dashboard/CompanyDashboardView.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: 'pending-offers',
            name: 'pending-offers',
            component: () => import('@/views/dashboard/DashboardOffersView.vue'),
            meta: { requiresAuth: true },
            props: { initialOption: 'pending-offers' },
          },
          {
            path: 'synergies',
            name: 'synergies',
            component: () => import('@/views/dashboard/DashboardOffersView.vue'),
            meta: { requiresAuth: true },
            props: { initialOption: 'synergies' },
          },
          {
            path: 'donations-dashboard',
            name: 'donations-dashboard',
            component: () => import('@/views/dashboard/DonationsDashboardView.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: 'portfolio-companies',
            name: 'portfolio-companies',
            component: () => import('@/components/authenticated/account/portfolio/PortfolioCompaniesManagement.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
    ],
  },
  {
    path: '/admin',
    name: 'admin',
    meta: { requiresUnAuth: true, requiresAdminUnAuth: true },
    component: () => import('@/views/AdminView.vue'),
    children: [
      {
        path: 'login',
        name: 'admin-login',
        meta: { requiresAdminUnAuth: true },
        component: () => import('@/components/admin/AdminLogin.vue'),
      },
      {
        path: 'panel',
        name: 'admin-panel',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/AdminPanel.vue'),
      },
      {
        path: 'users',
        name: 'admin-user-management',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/users/UserManagement.vue'),
      },
      {
        path: 'offerings',
        name: 'admin-offerings',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/offerings/OfferingsManagement.vue'),
      },
      {
        path: 'companies',
        name: 'admin-companies',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/companies/CompaniesManagement.vue'),
      },
      {
        path: 'statistics',
        name: 'admin-statistics',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/StatisticsManagement.vue'),
      },
      {
        path: 'queries',
        name: 'admin-queries',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/queries/QueriesManagement.vue'),
      },
      {
        path: 'categories',
        name: 'admin-categories',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/categories/CategoriesManagement.vue'),
      },
      {
        path: 'donations',
        name: 'admin-donations',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/donations/DonationsManagement.vue'),
      },
      {
        path: 'subscription-plans',
        name: 'admin-subscription-plans',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/subscription-plans/SubscriptionPlansManagement.vue'),
      },
      {
        path: 'trips',
        name: 'admin-trips',
        meta: { requiresAdminAuth: true },
        component: () => import('@/components/admin/trips/TripsManagement.vue'),
      },
    ],
  },
];

const liveRouter = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

liveRouter.beforeEach((to, from, next) => {
  // Check if this is a driver route
  if (to.meta.isDriverRoute) {
    const driverToken = localStorage.getItem('driver_token');
    
    // Driver authentication logic
    if (to.meta.requiresDriverAuth && !driverToken) {
      // Redirect to driver login if not authenticated
      next({ name: 'driver-login' });
    } else if (to.name === 'driver-login' && driverToken) {
      // Redirect to driver main view if already logged in
      next({ name: 'driver' });
    } else {
      next();
    }
    return;
  }

  // Existing authentication logic for main app
  const isAuthenticated = store.getters['auth/isAuthenticated'];
  const isAdmin = store.getters['auth/isAdmin'];
  const userRole = store.getters['auth/getUserRole'];

  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } });
  } else if (
    to.meta.requiresAuth &&
    isAuthenticated &&
    !isAdmin &&
    to.meta.requiresManager &&
    userRole !== 'MANAGER'
  ) {
    next(from.path);
  } else if (to.meta.requiresAuth && isAuthenticated && isAdmin) {
    next('/admin/panel');
  } else if (to.meta.requiresUnAuth && isAuthenticated) {
    next(isAdmin ? from.path : { path: '/login', query: { redirect: to.fullPath } });
  } else if (to.meta.requiresAdminAuth && (!isAuthenticated || !isAdmin)) {
    next(from.path);
  } else if (to.meta.requiresAdminUnAuth && isAdmin) {
    next('/admin/panel');
  } else if (to.matched.length === 0) {
    next('/');
  } else {
    next();
  }
});

export default liveRouter;
